import { html, LitElement, unsafeCSS, type CSSResultGroup, type PropertyValues, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { localiseLabel, localiseMarketingUrl, addUtmQueryParams } from '@/navbar/utils'
import { navbarContext, type NavbarPart } from '../context'
import type { EcoSuiteNavbarMenu } from '../menu/eco-suite-navbar-menu.component'
import { $t, localized, setLocale } from '@/navbar/i18n'
import { NAMESPACE_LABELS } from '@/navbar/constants/namespace-labels'
import fallbackMarketingData from '@/navbar/data/fallbackMarketingData'

import type {
  UserProduct,
  NavbarAppSwitcherMarketingData,
  NavbarAppSwitcherFallbackMarketingData,
  NavbarAppSwitcherTaxonomyNamespace,
  NavbarAppSwitcherTaxonomyCapability,
  NavbarAppSwitcherSimpleNamespace,
} from '@/navbar/navbar.types'

// Styles:
import styles from './eco-suite-navbar-app-switcher.scss?inline'

// Child components:
import '../menu/eco-suite-navbar-menu.component'
import '../tooltip/eco-suite-navbar-tooltip.component'
import '../menu-divider/eco-suite-navbar-menu-divider.component'
import '../app-switcher-search/eco-suite-navbar-app-switcher-search.component'
import '../app-switcher-ad/eco-suite-navbar-app-switcher-ad.component'

// Icons that don't use standard namespace identifiers:
const NAMESPACE_ICON_MAP: Record<string, string> = {
  'loneworker': 'staysafe',
  'esg': 'ecometrica',
  'permits': 'epermits',
  'training': 'munio',
  'chemical': 'chemical-manager',
  'coshh': 'sypol',
  'sds-auth': 'publisher',
  'sds-dist': 'reseller',
  'crisis': 'crisis-manager',
  'company_admin': 'administrator',
}

const tagName = 'eco-suite-navbar-app-switcher'

/**
 * @internal
 */
@localized()
@customElement(tagName)
export class EcoSuiteNavbarAppSwitcher extends LitElement {
  static readonly tagName = tagName
  static readonly styles: CSSResultGroup = [unsafeCSS(styles)]

  @consume({ context: navbarContext, subscribe: true })
  navbarContext?: NavbarPart

  @query('eco-suite-navbar-menu')
  private readonly menu!: EcoSuiteNavbarMenu

  @property({ type: Array })
  userProducts: UserProduct[] = []

  @property()
  currentProduct = ''

  @property()
  homeHref?: string

  @property({ attribute: false })
  marketingData?: NavbarAppSwitcherMarketingData

  @property()
  platformUrl?: string

  @state()
  private menuOpen = false

  @state()
  private spotlightNamespaces: NavbarAppSwitcherTaxonomyNamespace[] = []

  @state()
  private spotlightModules: NavbarAppSwitcherTaxonomyCapability[] = []

  @state()
  private searchOpen = false

  connectedCallback(): void {
    super.connectedCallback()

    if (!this.userProducts?.length) this.addCurrentProduct()
  }

  protected willUpdate(changedProps: PropertyValues) {
    if (changedProps.has('menuOpen')) {
      if (this.menuOpen) {
        if (this.marketingData) {
          if (!this.spotlightNamespaces.length) this.setSpotlightNamespaces()

          // Set the spotlight modules on each open so they're always random
          this.setSpotlightModules()
        }
      } else {
        this.searchOpen = false
      }
    }

    if ((changedProps.has('userProducts') || changedProps.has('marketingData')) && this.spotlightNamespaces.length) {
      this.spotlightNamespaces = []
    }

    if (changedProps.has('userProducts') && !this.userProducts?.length) {
      this.addCurrentProduct()
    }
  }

  /** Used in the taxonomy editor preview. */
  public setLocale(locale: string) {
    setLocale(locale)
  }

  private addCurrentProduct() {
    if (!this.currentProduct?.length) return

    this.userProducts = [
      ...(this.userProducts ?? []),
      {
        identifier: this.currentProduct,
        links: {
          goToApplication: this.homeHref ?? '/',
        },
      },
    ]
  }

  public focus() {
    this.menu.focusButton()
  }

  private setSpotlightNamespaces() {
    if (!this.marketingData) return
    const { namespaces } = this.marketingData
    if (!Array.isArray(namespaces)) return

    // We spotlight namespaces the user doesn't have access to but are relevant to them based on their current products
    this.spotlightNamespaces = namespaces
      .filter(ns => {
        return !this.getUserNamespace(ns.identifier)
          && (ns.whitespace.includes('all') || ns.whitespace.some(w => this.getUserNamespace(w)))
      }).sort((a, b) => a.display_order - b.display_order)
  }

  private setSpotlightModules() {
    if (!this.marketingData) return
    const { capabilities } = this.marketingData
    if (!Array.isArray(capabilities)) return

    // First sort the modules randomly:
    const randomCapabilities = [...capabilities].sort(() => 0.5 - Math.random())

    // Then pick a module from each spotlight namespace (already sorted by display order) if available:
    this.spotlightModules = this.spotlightNamespaces.map(ns => {
      return randomCapabilities.find(c => c.namespace_identifier === ns.identifier)
    })
      .filter(Boolean)
      .slice(0, 5) as NavbarAppSwitcherTaxonomyCapability[]

    if (this.spotlightModules.length < 5) {
      // Then filter for other modules the user doesn't have access to and take as many as needed to fill the spotlight:
      const additionalModules = randomCapabilities.filter(c => {
        return !this.getUserNamespace(c.namespace_identifier)
          && !this.spotlightModules.some(ms => ms.labels.en === c.labels.en)
      }).slice(0, 5 - this.spotlightModules.length)

      this.spotlightModules = [...this.spotlightModules, ...additionalModules]
    }
  }

  private getUserProduct(productIdentifier: string): UserProduct | undefined {
    return this.userProducts?.find(up => up.identifier === productIdentifier)
  }

  /**
   * Returns the first UserProduct the user owns from the given products list.
   * Works for both full taxonomy namespaces and simple fallback namespaces.
   */
  private getUserProductForNamespace(products: string[]): UserProduct | undefined {
    return products.map(p => this.getUserProduct(p)).find(Boolean)
  }

  /**
   * Returns a UserProduct if the user owns any product belonging to the given namespace.
   */
  private getUserNamespace(namespaceIdentifier: string): UserProduct | undefined {
    if (!this.marketingData) return undefined
    const ns = this.marketingData.namespaces.find(n => n.identifier === namespaceIdentifier)
    if (!ns) return undefined
    return this.getUserProductForNamespace(ns.products)
  }

  private isSpotlightNamespace(namespaceIdentifier: string): boolean {
    return this.spotlightNamespaces.some(ns => ns.identifier === namespaceIdentifier)
  }

  private getIconName(namespaceIdentifier: string): string {
    return NAMESPACE_ICON_MAP[namespaceIdentifier] ?? namespaceIdentifier
  }

  private renderNamespaceItem(namespace: NavbarAppSwitcherTaxonomyNamespace | NavbarAppSwitcherSimpleNamespace) {
    const identifier = namespace.identifier
    const userProduct = this.getUserProductForNamespace(namespace.products)
    const isCurrent = !!userProduct && userProduct.identifier === this.currentProduct

    const label = NAMESPACE_LABELS[identifier] ?? identifier
    const iconName = this.getIconName(identifier)

    let href: string
    if (userProduct?.links?.goToApplication) {
      href = userProduct.links.goToApplication
    } else if ('marketing_urls' in namespace) {
      const marketingUrl = localiseMarketingUrl(namespace.marketing_urls) ?? ''
      href = addUtmQueryParams(marketingUrl, this.currentProduct, 'navbar-app-switcher')
    } else {
      const marketingUrl = namespace.marketing_url ?? ''
      href = addUtmQueryParams(marketingUrl, this.currentProduct, 'navbar-app-switcher')
    }

    return html`
      <a
        class="AppSwitcher__item"
        href=${href}
        target=${userProduct ? '_self' : '_blank'}
        rel=${userProduct ? nothing : 'noopener noreferrer'}
        aria-label=${userProduct ? label : `${label}, ${$t('navbar.opensInNewTab')}`}
        aria-current=${isCurrent ? 'page' : nothing}
      >
        <div class="AppSwitcher__item-icon ${userProduct ? 'AppSwitcher__item-icon--user-active' : ''}">
          <pwc-icon-shadow icon=${iconName} size="sm" aria-hidden="true"></pwc-icon-shadow>
        </div>
        <span class="AppSwitcher__item-label ${userProduct ? 'AppSwitcher__item-label--user-active' : ''}">
          ${label}
        </span>
        <pwc-icon-shadow
          class="AppSwitcher__item-link-type"
          icon=${userProduct ? 'arrow-right' : 'external-link'}
          size="xs"
          aria-hidden="true"
        ></pwc-icon-shadow>
      </a>
    `
  }

  render() {
    let allItems: (NavbarAppSwitcherTaxonomyNamespace | NavbarAppSwitcherSimpleNamespace)[],
      headerText: string,
      bodyText: string,
      exploreMoreUrl: string

    // company_admin: special case — kept outside namespace logic
    const hasCompanyAdmin = this.getUserProduct('company_admin')
    const companyAdminItem: NavbarAppSwitcherSimpleNamespace = {
      identifier: 'company_admin',
      marketing_url: '',
      products: ['company_admin'],
    }

    if (this.marketingData) {
      const data = this.marketingData
      const namespaces = data.namespaces
      const sort = (a: NavbarAppSwitcherTaxonomyNamespace, b: NavbarAppSwitcherTaxonomyNamespace) => a.display_order - b.display_order
      const userNamespaces = namespaces.filter(ns => this.getUserNamespace(ns.identifier)).sort(sort)
      const spotlightNamespaces = this.spotlightNamespaces
      const otherNamespaces = namespaces.filter(ns =>
        !this.getUserNamespace(ns.identifier) && !this.isSpotlightNamespace(ns.identifier) && ns.default_show,
      ).sort(sort)

      allItems = [
        ...userNamespaces,
        ...(hasCompanyAdmin ? [companyAdminItem] : []),
        ...spotlightNamespaces,
        ...otherNamespaces,
      ]

      headerText = localiseLabel(data.header_text, $t('navbar.ad.headerText')) ?? ''
      bodyText = localiseLabel(data.body_text, $t('navbar.ad.bodyText')) ?? ''
      exploreMoreUrl = localiseMarketingUrl(data.explore_more_urls) ?? ''
    } else {
      const data = fallbackMarketingData as NavbarAppSwitcherFallbackMarketingData
      const { namespaces } = data
      const userNamespaces = namespaces.filter(ns => this.getUserProductForNamespace(ns.products))
      const otherNamespaces = namespaces.filter(ns => !this.getUserProductForNamespace(ns.products))

      allItems = [
        ...userNamespaces,
        ...(hasCompanyAdmin ? [companyAdminItem] : []),
        ...otherNamespaces,
      ]

      headerText = data.header_text
      bodyText = data.body_text
      exploreMoreUrl = data.explore_more_url
    }

    return html`
      <eco-suite-navbar-menu
        icon="app-switcher"
        label=${$t('appSwitcher.switchProduct')}
        .hideMenuHeader=${true}
        .isAppSwitcher=${true}
        .hasMenu=${true}
        .forceNavbarContext=${'desktop'}
        data-qa-id="app-switcher"
        @eco-after-show=${() => this.menuOpen = true}
        @eco-after-hide=${() => this.menuOpen = false}
      >
        <div class="AppSwitcher ${this.searchOpen ? 'AppSwitcher--searching' : ''}">
          <div class="AppSwitcher__left">
            ${
              this.marketingData ? html`
                <eco-suite-navbar-app-switcher-search
                  .userProducts=${this.userProducts}
                  .marketingData=${this.marketingData}
                  .currentProduct=${this.currentProduct}
                  ?open=${this.searchOpen}
                  @eco-search-opened=${() => this.searchOpen = true}
                  @eco-search-closed=${() => this.searchOpen = false}
                ></eco-suite-navbar-app-switcher-search>
              ` : nothing
            }

            <ul class="AppSwitcher__list">
              ${allItems.map(item => html`<li>${this.renderNamespaceItem(item)}</li>`)}

              <li>
                <a
                  class="AppSwitcher__all-apps"
                  href=${this.platformUrl?.length ? this.platformUrl : exploreMoreUrl}
                  target=${this.platformUrl?.length ? '_self' : '_blank'}
                  rel=${this.platformUrl?.length ? nothing : 'noopener noreferrer'}
                  aria-label=${this.platformUrl?.length ? $t('appSwitcher.allApps') : `${$t('appSwitcher.allApps')}, ${$t('navbar.opensInNewTab')}`}
                >
                  <span>${$t('appSwitcher.allApps')}</span>
                  <pwc-icon-shadow icon="arrow-right" size="xs" aria-hidden="true"></pwc-icon-shadow>
                </a>
              </li>
            </ul>
          </div>

          <eco-suite-navbar-app-switcher-ad
            .headerText=${headerText}
            .bodyText=${bodyText}
            .exploreMoreUrl=${exploreMoreUrl}
            .taxonomyCapabilities=${this.spotlightModules}
            .currentProduct=${this.currentProduct}
            ?inert=${this.searchOpen}
          ></eco-suite-navbar-app-switcher-ad>
        </div>
      </eco-suite-navbar-menu>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eco-suite-navbar-app-switcher': EcoSuiteNavbarAppSwitcher
  }
}
